import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import { useForm } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface ClaudeAuthorization {
  id: number
  agent_id: number | null
  agent_username: string | null
  status: string
  account_email: string | null
  account_display_name: string | null
  organization_name: string | null
  subscription_type: string | null
  scopes: string[] | null
  expires_at: string | null
  last_refreshed_at: string | null
  created_at: string
}

interface Agent {
  id: number
  username: string
}

interface Props {
  authorization: ClaudeAuthorization
  agents: Agent[]
}

export default function Edit({ authorization, agents }: Props) {
  const { data, setData, patch, processing } = useForm({
    'claude_authorization.agent_id': authorization.agent_id ?? '',
    'claude_authorization.account_email': authorization.account_email ?? '',
    'claude_authorization.account_display_name': authorization.account_display_name ?? '',
    'claude_authorization.organization_name': authorization.organization_name ?? '',
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/manage/claude_authorizations/${authorization.id}`)
  }

  return (
    <SidebarLayout section="manage" title={`Edit Authorization #${authorization.id}`}>
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', maxWidth: 600 }}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip
            label={authorization.status}
            color={authorization.status === 'active' ? 'success' : authorization.status === 'expired' ? 'error' : 'default'}
            size="small"
          />
          {authorization.subscription_type && (
            <Typography variant="body2" color="text.secondary">
              Plan: {authorization.subscription_type}
            </Typography>
          )}
          {authorization.expires_at && (
            <Typography variant="body2" color="text.secondary">
              Expires: {new Date(authorization.expires_at).toLocaleString()}
            </Typography>
          )}
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Assigned Agent</InputLabel>
            <Select
              value={data['claude_authorization.agent_id']}
              onChange={(e) => setData('claude_authorization.agent_id', e.target.value as number | '')}
              label="Assigned Agent"
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Account Email"
            value={data['claude_authorization.account_email']}
            onChange={(e) => setData('claude_authorization.account_email', e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Display Name"
            value={data['claude_authorization.account_display_name']}
            onChange={(e) => setData('claude_authorization.account_display_name', e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Organization"
            value={data['claude_authorization.organization_name']}
            onChange={(e) => setData('claude_authorization.organization_name', e.target.value)}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button type="submit" variant="contained" disabled={processing}>
              Save
            </Button>
            <Button variant="outlined" href="/manage/claude_authorizations" component="a">
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </SidebarLayout>
  )
}
